import React, { useEffect, useState } from 'react'
import { Box, Button, colors, List, ListItem, ListItemText, Paper, Typography } from '@mui/material'
import { Cancel, CheckCircle, PlayCircleFilled, Share } from '@mui/icons-material'
import { LogItemMessage, MachineStateChanged, RecipeActionMessage } from '../../state/sagas/app'
import { MachineReducer, MachineState, SignalRLogReducer } from '../../state/reducers/machine'
import { SubpageTitle } from '../../components/atoms/text/titles'
import { AnimatePresence, motion } from 'framer-motion'
import { DateTime } from 'luxon'
import { LogLevelIcon } from '../../components/molecules/logs/logLevelIcon'
import { LevelType } from '../../components/molecules/logs/logLevelDisplay'
import { AppDispatch, StoreState } from '../../state/configureStore'
import { AnyAction, Dispatch } from 'redux'
import { connect, useDispatch } from 'react-redux'
import { testMachineConnected, testRecipe } from '../../state/actions/machine'
import theme from '../../theme'
import { isProd } from '../../config'
import { DimensionsReducer } from '../../state/reducers/app'
import TopListComponent, { TopListItem } from './topListComponent'
import { fetchTopCustomers, fetchTopStores } from '../../state/thunks/customer'
import { TopItem } from '../../state/reducers/customer'
import { FakeTriggerSales } from '../../components/fakeTriggerSale'

function getReadableDateTime(dateTime: DateTime) {
  if (!dateTime.isValid) return '-'

  const difference = DateTime.now().diff(dateTime)
  if (difference.as('minutes') < 1) return 'Now'
  if (difference.as('minutes') < 60) return `${difference.as('minutes').toFixed(0)} minutes ago`
  if (difference.as('days') < 1) return `${dateTime.toFormat('hh:mm')}`

  return `${dateTime.toFormat('d LLL hh:mm')}`
}

const getMachineStates = (machines: MachineState[]) => {
  const totalCycles = machines.reduce((prev, c) => prev += c.cyclesCountToday, 0)
  const machineStates = {
    cycles: totalCycles,
    machineCount: machines.length,
    average: Number(totalCycles / machines.length).toFixed(2)
  }

  return machineStates
}


const styles = {
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    maxHeight: '100%',
    overflow: 'auto',
    marginTop: 2
  }
}

export type MapSideBarDisplayProps = {
  machines: MachineState[]
  signalRLogs: SignalRLogReducer
  all_machines: MachineReducer,
  dimensions: DimensionsReducer,
  testMachineConnected: (message) => void
  testRecipe: (message) => void,
  horizontal: boolean
  topCustomers: TopItem[],
  topStores
}

type LogObject = {
  log: RecipeActionMessage | LogItemMessage | MachineStateChanged,
  logType: 'RecipeActionMessage' | 'LogItemMessage' | 'MachineStateChanged',
  serialId: string
}

let testState

// TODO: soft transition when new log comes in
function MapSideBarDisplay({ machines, all_machines, dimensions, topCustomers, topStores, testMachineConnected, testRecipe, horizontal }: MapSideBarDisplayProps) {

  const [selectedTab, setSelectedTab] = useState<'today' | 'rolling'>('today');

  const dispatch = useDispatch<AppDispatch>()

  useEffect(() => {
    dispatch(fetchTopCustomers(28))
    dispatch(fetchTopStores(28))
  }, [])



  let logs: LogObject[] = []
  const dateToday = DateTime.now().toFormat('cccc d LLLL')
  const machineStates = getMachineStates(machines)
  const isDesktop = dimensions.device === 'desktop'





  const prepareMachines = () => {
    const stores: Array<TopListItem> = []
    all_machines.map((store) => stores.push(
      {
        name: store.nodeName,
        countNumber: store.cyclesCountToday,
      }))
    return stores
  }

  const prepareTopCustomers = () => {
    const customers: Array<TopListItem> = []
    topCustomers.map((customer) => customers.push(
      {
        name: customer.name,
        countNumber: customer.recipesCount,
      }))
    return customers
  }

  const prepareTopStores = () => {
    const stores: Array<TopListItem> = []
    topStores.map((store) => stores.push(
      {
        name: store.name,
        countNumber: store.recipesCount,
      }))
    return stores
  }


  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: (horizontal || !isDesktop) ? 'fit-content' : '100%',
      paddingTop: !isDesktop ? 1 : 2,
      alignItems: 'flex-start',
      backgroundColor: theme.palette.background.paper,
    }}>
      <SubpageTitle title={dateToday} />
      <Box sx={{ display: "flex", gap: 2, marginBottom: 1 }}>
        <Button
          onClick={() => setSelectedTab('today')}
          sx={{
            borderRadius: 0,
            color: selectedTab === "rolling" ? "#808080" : "black",
            borderBottom: selectedTab === 'today' ? '2px solid black' : '2px solid transparent',
            '&:hover': {
              backgroundColor: 'transparent',
            }
          }}
        >
          Today
        </Button>

        <Button
          onClick={() => setSelectedTab('rolling')}
          sx={{
            borderRadius: 0,
            color: selectedTab === "today" ? "#808080" : "black",
            borderBottom: selectedTab === 'rolling' ? '2px solid black' : '2px solid transparent',
            '&:hover': {
              backgroundColor: 'transparent',
            }
          }}
        >
          Rolling 28 days
        </Button>
      </Box>

      {selectedTab === "today" ? (
        <div style={{ display: "flex", gap: 16, width: "100%", flexDirection: "column" }} >
          <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center', marginTop: 2 }}>
            <Paper sx={{
              width: "100%",
              padding: "16px 32px",
              boxShadow: '0 2px 6px 1px rgba(222,222,222,0.50)',
              borderRadius: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              gap: !isDesktop ? 1 : 'auto',
              flexWrap: isDesktop ? 'nowrap' : 'wrap'
            }}>
              <Box>
                <Typography variant={!isDesktop ? "h5" : "h4"} sx={{ lineHeight: 0.8 }}>{machineStates.cycles}</Typography>
                <Typography variant={!isDesktop ? "caption" : "body2"} style={{ color: theme.palette.text.secondary }} >{"recipes"}</Typography>
              </Box>
              <Box>
                <Typography variant={!isDesktop ? "h5" : "h4"} sx={{ lineHeight: 0.8 }}>{machineStates.machineCount}</Typography>
                <Typography variant={!isDesktop ? "caption" : "body2"} style={{ color: theme.palette.text.secondary }}>{"machines"}</Typography>
              </Box>
              <Box>
                <Typography variant={!isDesktop ? "h5" : "h4"} sx={{ lineHeight: 0.8 }}>{machineStates.average}</Typography>
                <Typography variant={!isDesktop ? "caption" : "body2"} style={{ color: theme.palette.text.secondary }}>{"average"}</Typography>
              </Box>
              {/* <FakeTriggerSales /> */}
            </Paper>
          </Box>
          <TopListComponent
            items={prepareMachines()}
            dimensions={dimensions}
            title={"Top 10 stores"}
            container
            starIcon
          />
        </div>
      ) : (
        <div style={{ display: "flex", width: "100%", gap: 16, flexDirection: "column", marginTop: 16 }} >
          <TopListComponent
            items={prepareTopStores()}
            dimensions={dimensions}
            title={"Top 10 stores"}
            container
            starIcon

          />

          <TopListComponent
            items={prepareTopCustomers()}
            dimensions={dimensions}
            title={"Top customers"}
            container
            starIcon
          />
        </div>
      )

      }

      <Box sx={styles.listContainer}>
        {logs?.length ?
          <React.Fragment>
            <Typography sx={{ fontFamily: 'VogieBold' }} variant={"h6"}>Live feed</Typography>
            <List disablePadding sx={{ height: '100%', width: '100%', overflow: 'auto' }}>
              {logs.map((logObj, index) => {
                const machine = all_machines?.find(d => d.serialId === logObj.log.machineId)
                if (!machine)
                  return null

                const listItemComponent = getLogListItem(logObj, machine, all_machines, index, isDesktop)

                return listItemComponent ?
                  <AnimatePresence key={'listItem' + index}>
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}>
                      {listItemComponent}
                    </motion.div>
                  </AnimatePresence>
                  : null
              })}
            </List>
          </React.Fragment>
          : null
        }
      </Box>

      {
        !isProd() && (
          <Paper sx={{ width: "100%", padding: "16px 32px", boxShadow: '0 2px 6px 1px rgba(222,222,222,0.50)', borderRadius: '20px', display: 'flex', flexDirection: 'column' }}>
            <Button variant={"contained"} onClick={() => {
              testState = testState === 'Connected' ? 'Disconnected' : 'Connected'

              testMachineConnected({
                machineId: 'test0001',
                currentState: testState,
                timestamp: DateTime.now().toISO()
              })
            }}>Toggle Connection</Button>
            <Button sx={{ marginTop: 6 }} variant={"contained"} onClick={() => {
              testState = testState === 'Connected' ? 'Disconnected' : 'Connected'

              testRecipe({
                machineId: 'test0001',
                action: 'started',
                // nodeId: string,
                recipeType: 'Suede',
                timestamp: DateTime.now().toISO()
              })
            }}>Run recipe</Button>
          </Paper>
        )
      }
    </Box>
  )
}

const primaryTypographyProps = { fontSize: '15px' }
const secondaryTypographyProps = { fontSize: '13px' }
const LogListItem = ({ icon, isDesktop, key, primaryStr, secondaryStr }:
  { icon: React.JSX.Element | null, isDesktop: boolean, key: string, primaryStr: string, secondaryStr: React.JSX.Element | string }) => {

  return (
    <ListItem sx={{ display: 'flex', flexDirection: 'row', gap: !isDesktop ? 1 : 'auto' }} disablePadding >
      <Box sx={{ flex: 1 }}>
        {icon}
      </Box>
      <Box sx={{ flex: 10 }}>
        <ListItemText
          primaryTypographyProps={primaryTypographyProps}
          secondaryTypographyProps={secondaryTypographyProps}
          primary={primaryStr}
          secondary={secondaryStr} />
      </Box>
    </ListItem>)
}

const getLogListItem = (logObj: LogObject, machine: MachineState, machines: MachineReducer, index: number, isDesktop: boolean) => {
  const dateAndIdStr = getReadableDateTime(DateTime.fromISO(logObj.log.timestamp))

  if (logObj.logType === 'RecipeActionMessage') {
    const log = logObj.log as RecipeActionMessage

    const actionString = log.action === 'started' ? log.action
      : log.action === 'done' ? 'is done with'
        : log.action

    const icon = (
      log.action === 'started' ? <PlayCircleFilled color={'success'} />
        : log.action === 'done' ? < CheckCircle color={'success'} />
          : log.action === 'canceled' ? <Cancel color={'warning'} />
            : null
    )

    return <LogListItem
      icon={icon}
      isDesktop={isDesktop}
      //      key={'log_' + index}
      primaryStr={machine.name + ' ' + actionString + ' a ' + log.recipeType + ' recipe'}
      secondaryStr={dateAndIdStr}
    />
  }
  else if (logObj.logType === 'LogItemMessage') {
    const log = logObj.log as LogItemMessage
    const machine = machines?.find(d => d.serialId === log.machineId)
    if (!machine) return null

    const primaryStr = 'Machine "' + machine.name + '" reported log of level "' + log.level + '"'
    const secondaryStr = 'Message: ' + log.message

    return <LogListItem
      icon={<LogLevelIcon level={log.level as LevelType} small />}
      isDesktop={isDesktop}
      key={'log_' + index}
      primaryStr={primaryStr}
      secondaryStr={<React.Fragment>
        <Typography sx={secondaryTypographyProps}>{secondaryStr}</Typography>
        <Typography sx={secondaryTypographyProps}>{dateAndIdStr}</Typography>
      </React.Fragment>}
    />
  }
  else if (logObj.logType === 'MachineStateChanged') {
    const log = logObj.log as MachineStateChanged
    const machine = machines?.find(d => d.serialId === log.machineId)

    if (!machine) return null

    // eslint-disable-next-line no-useless-concat
    const primaryStr = 'Machine "' + machine.name + '" has been ' + `${log.currentState === 'Disconnected' ? 'disconnected from' : 'connected to'}` + ' the IoT hub.'

    return <LogListItem
      icon={<Share color={log.currentState === 'Disconnected' ? 'error' : 'success'} />}
      isDesktop={isDesktop}
      key={'log_' + index}
      primaryStr={primaryStr}
      secondaryStr={dateAndIdStr}
    />
  }
}

const mapStateToProps = (state: StoreState) => ({
  signalRLogs: state.signal_r_logs,
  all_machines: state.machines,
  fetchingState: state.machine_fetching,
  dimensions: state.dimensions,
  topCustomers: state.top_customers.data,
  topStores: state.top_store.data

})

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) => ({
  // fetchMachines: () => dispatch(fetchMachines()),
  testMachineConnected: (message) => dispatch(testMachineConnected(message)),
  testRecipe: (message) => dispatch(testRecipe(message)),
})

export default connect(mapStateToProps, mapDispatchToProps)(MapSideBarDisplay)